import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';

// ── Animation helpers ────────────────────────────────────
const fadeUp = (delay: number, v: boolean): React.CSSProperties => ({
  opacity: v ? 1 : 0,
  transform: v ? 'translateY(0)' : 'translateY(20px)',
  filter: v ? 'blur(0px)' : 'blur(7px)',
  transition: `opacity 0.9s ease ${delay}ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}ms, filter 0.9s ease ${delay}ms`,
});

const revealUp = (delay: number, v: boolean): React.CSSProperties => ({
  display: 'block',
  transform: v ? 'translateY(0)' : 'translateY(110%)',
  transition: `transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
});

const slideX = (delay: number, v: boolean, dir: 1 | -1 = 1): React.CSSProperties => ({
  opacity: v ? 1 : 0,
  transform: v ? 'translateX(0)' : `translateX(${dir * 40}px)`,
  transition: `opacity 1s ease ${delay}ms, transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
});

export default function Brand() {
  const { ref: leftRef, isVisible: lv } = useScrollAnimation(0.08);
  const { ref: rightRef, isVisible: rv } = useScrollAnimation(0.08);
  const { ref: cardsRef, isVisible: cv } = useScrollAnimation(0.05);
  const { t } = useTranslation();

  const values = [
    {
      icon: 'ri-equalizer-3-line',
      title: t('brand_v1_title'),
      desc: t('brand_v1_desc'),
    },
    {
      icon: 'ri-eye-off-line',
      title: t('brand_v2_title'),
      desc: t('brand_v2_desc'),
    },
    {
      icon: 'ri-contrast-drop-line',
      title: t('brand_v3_title'),
      desc: t('brand_v3_desc'),
    },
  ];

  return (
    <section id="brand" className="w-full bg-[#FAF8F5] py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* ── Two-Column: Left text, Right image ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start mb-24">

          {/* Left Column */}
          <div ref={leftRef} className="flex flex-col">
            <p className="font-label text-xs tracking-[0.3em] text-stone-400 uppercase mb-4"
               style={fadeUp(0, lv)}>
              {t('brand_label')}
            </p>

            <div className="overflow-hidden mb-1">
              <h2 className="font-zh-serif text-3xl md:text-4xl lg:text-5xl text-stone-900 font-light"
                  style={revealUp(150, lv)}>
                {t('brand_title')}
              </h2>
            </div>

            <p className="font-label text-sm tracking-[0.2em] text-stone-400 italic mb-5"
               style={fadeUp(300, lv)}>
              {t('brand_italic')}
            </p>

            <div className="mb-10 h-px overflow-hidden" style={{ width: '3rem' }}>
              <div className="h-full bg-stone-300 origin-left"
                   style={{
                     transform: lv ? 'scaleX(1)' : 'scaleX(0)',
                     transition: `transform 1s cubic-bezier(0.16,1,0.3,1) 440ms`,
                   }} />
            </div>

            <div className="overflow-hidden mb-1">
              <span className="font-zh-serif text-2xl md:text-3xl lg:text-4xl text-stone-800 font-light leading-relaxed"
                    style={revealUp(560, lv)}>
                {t('brand_h3_1')}
              </span>
            </div>
            <div className="overflow-hidden mb-8">
              <span className="font-zh-serif text-2xl md:text-3xl lg:text-4xl text-stone-500 font-light leading-relaxed"
                    style={revealUp(700, lv)}>
                {t('brand_h3_2')}
              </span>
            </div>

            <p className="font-noto text-stone-500 text-base leading-8 mb-6 font-light"
               style={fadeUp(860, lv)}>
              {t('brand_p1')}
            </p>
            <p className="font-noto text-stone-500 text-base leading-8 mb-10 font-light"
               style={fadeUp(980, lv)}>
              {t('brand_p2')}
            </p>

            <div style={fadeUp(1080, lv)}>
              <button
                className="px-8 py-3 border border-stone-900 text-stone-900 text-sm tracking-wide rounded-full hover:bg-stone-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                onClick={() => {
                  const section = document.getElementById('nimo-video-section');
                  const video = document.getElementById('nimo-native-video') as HTMLVideoElement | null;
                  if (section) {
                    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                  if (video) {
                    setTimeout(() => {
                      video.play().catch(() => {});
                    }, 600);
                  }
                }}
              >
                {t('brand_cta')}
              </button>
            </div>
          </div>

          {/* Right Column */}
          <div ref={rightRef} style={slideX(200, rv, 1)}>
            <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/f79e47cb-8207-43be-b401-efac4642191e_-.jpg?v=fa0f0ecaaa0b4590575a63444f9e1cba"
                alt="NIMO AI Brand"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <p className="font-zh-serif text-white/90 text-lg font-light italic">
                  {t('brand_quote')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Value Cards ── */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone-200 rounded-2xl overflow-hidden">
          {values.map((v, i) => (
            <div
              key={v.title}
              className="group relative bg-[#FAF8F5] p-10 hover:bg-white transition-colors duration-500 cursor-default overflow-hidden"
              style={fadeUp(i * 130, cv)}
            >
              <div className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full bg-stone-900 transition-all duration-500" />

              <span
                className="absolute top-6 right-7 font-label text-[4.5rem] font-bold leading-none text-stone-900/[0.05] select-none group-hover:text-stone-900/[0.09] transition-colors duration-500"
                aria-hidden="true"
              >
                {String(i + 1).padStart(2, '0')}
              </span>

              <div className="w-12 h-12 flex items-center justify-center rounded-xl border border-stone-200 bg-white mb-8 relative z-10 group-hover:border-stone-400 group-hover:bg-stone-50 transition-all duration-300">
                <i className={`${v.icon} text-xl text-stone-700 group-hover:text-stone-900 transition-colors duration-300`} />
              </div>

              <div className="w-6 h-px bg-stone-300 mb-6 group-hover:w-10 group-hover:bg-stone-500 transition-all duration-400 relative z-10" />

              <h4 className="font-zh-serif text-xl text-stone-900 font-light mb-4 relative z-10">{v.title}</h4>

              <p className="font-noto text-stone-400 text-sm leading-7 font-light relative z-10 group-hover:text-stone-500 transition-colors duration-300">{v.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
